// =============================================================================
// 基于 docs/ui_sidebar/templates/03-useNavVisibility.ts.template(逐字保留)
// 根据当前用户 permissions 过滤菜单；后端 RBAC 才是安全边界，
// 本 hook 仅做"可用性"隐藏。
// =============================================================================

import { useMemo } from 'react'
import type { NavGroup, NavItem } from './schema'
import { useAuthStore } from '../store/auth'

/**
 * 空权限常量:必须模块级稳定引用。
 * zustand v5 底层用 useSyncExternalStore,靠 Object.is 判定 getSnapshot 结果是否变化;
 * 若选择器每次返回新数组(如 `?? []`),未登录时引用永远变化 → 无限重渲染(React #185)。
 * 这里复用同一个常量数组即可。
 */
const NO_PERMISSIONS: string[] = []

/** 单项权限判定："*" 视为全部 */
export function hasPermission(userPermissions: string[] | undefined, required?: string): boolean {
  if (!required) return true // 未声明权限 = 仅登录即可访问
  if (!userPermissions || userPermissions.length === 0) return false
  if (userPermissions.includes('*')) return true
  return userPermissions.includes(required)
}

/**
 * 过滤后的菜单组。
 * - 剔除当前用户无权限的 NavItem；
 * - 剔除后整组为空的 NavGroup；
 * - 按 NavGroup.order 升序排列；
 * - user.permissions 引用不变时返回相同引用（useMemo 缓存）。
 */
export function useNavVisibility(groups: NavGroup[]): NavGroup[] {
  const permissions = useAuthStore((s) => s.user?.permissions ?? NO_PERMISSIONS)

  return useMemo(() => {
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((i: NavItem) => hasPermission(permissions, i.permission)),
      }))
      .filter((g) => g.items.length > 0)
      .sort((a, b) => a.order - b.order)
  }, [groups, permissions])
}